import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import ButtonPrimary, {
  EButtonSize,
} from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import { FullScreenLoadingState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState2";
import { PaymentRequestFetch } from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import { PropertyFetchListItem } from "@rentiohq/shared-frontend/dist/components/components/PropertyFetchListItem";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import rentioTheme from "@rentiohq/shared-frontend/dist/components/theme/theme.rentio";
import * as bankAccountHooks from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.hooks";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import {
  EPaymentMethod,
  EPaymentRepetitionType,
  IBankWirePayment,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { IPaymentRequest } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { ERentDepositProvider } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatIban } from "@rentiohq/shared-frontend/dist/utils/iban.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import assets from "assets";
import withAuthenticationCheck from "hoc/withAuthenticationCheck";
import withKycRequired from "hoc/withKycRequired";
import QRCode from "qrcode.react";
import React from "react";
import { useNavigate, useParams } from "react-router";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import PaymentRequestRedirect from "../../components/PaymentRequestRedirect";
import * as infoUtils from "../../utils/info.utils";
import * as S from "./BankWireOverview.styles";

interface IProps {
  paymentMethod: EPaymentMethod;
}

const BankWireOverviewBase = (props: IProps) => {
  const { paymentMethod } = props;

  const navigate = useNavigate();
  const params = useParams<{ paymentRequestId?: string }>();

  const [bankWirePayment, setBankWirePayment] = React.useState<
    IBankWirePayment | undefined
  >(undefined);
  const [fetchBankWireError, setFetchBankWireError] = React.useState<
    Error | undefined
  >(undefined);
  const [isFetchingBankWire, setIsFetchingBankWire] = React.useState(false);
  const [didCompleteBankWire, setDidCompleteBankWire] = React.useState(false);

  // Data
  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const { detail: paymentRequest } = paymentRequestHooks.useDetail({
    id: paymentRequestId,
    shouldRefetch: false,
  });

  const isBanqupPayment = paymentRequestUtils.isBanqupPayment(paymentRequest);

  const fetchBankWire = async () => {
    if (!paymentRequestId || !paymentRequest) {
      return;
    }

    if (paymentMethod !== EPaymentMethod.BankWire) {
      return;
    }

    try {
      setIsFetchingBankWire(true);

      const { data } = isBanqupPayment
        ? await paymentRequestApi.generateBanqupBankWirePayment(
            paymentRequestId,
          )
        : await paymentRequestApi.generateBankWirePayment(paymentRequestId);

      setIsFetchingBankWire(false);
      setBankWirePayment(data);
    } catch (unknownError) {
      const error = unknownError as any;

      setIsFetchingBankWire(false);
      setFetchBankWireError(error);
    }
  };

  const {
    detail: ibanisationBankAccount,
    isFetching: isFetchingIbanisationBankAccount,
    fetchError: fetchIbanisationError,
    refetch: fetchIbanisationBankAccount,
  } = bankAccountHooks.useDetail({
    shouldRefetch: false,
    skipLegacy: true,
    customPath:
      // Only fetch if the payment method is Ibanisation
      paymentRequestId && paymentMethod === EPaymentMethod.Ibanisation
        ? `/payment-requests/${paymentRequestId}/ibanisation-bank-account`
        : undefined,
  });

  // Lifecycle
  React.useEffect(() => {
    if (paymentRequest) {
      fetchBankWire();
    }
  }, [paymentRequest]);

  // Event handlers
  const handlePressMoreInfo = () => {
    const url = getLocalizedText("payment.online.bank_wire.cta.info.url");
    window.open(url, "_blank");
  };

  const handlePressPaid = async () => {
    setDidCompleteBankWire(true);
  };

  const handlePressOtherMethod = () => {
    if (!paymentRequestId) {
      return;
    }

    navigate(`/payment/${paymentRequestId}/payment-method`);
  };

  const handlePressDoneBack = () => {
    setDidCompleteBankWire(false);
  };

  // Render
  const renderPaymentRequestAndProperty = (
    paymentRequest: IPaymentRequest,
    property?: IProperty,
  ) => {
    if (paymentMethod === EPaymentMethod.BankWire) {
      if (isFetchingBankWire) {
        return renderLoading();
      }

      if (fetchBankWireError) {
        return renderError(fetchBankWire, fetchBankWireError);
      }
    }

    if (paymentMethod === EPaymentMethod.Ibanisation) {
      if (isFetchingIbanisationBankAccount) {
        return renderLoading();
      }

      if (fetchIbanisationError) {
        return renderError(fetchIbanisationBankAccount, fetchIbanisationError);
      }
    }

    if (didCompleteBankWire) {
      return (
        <Page backgroundColor={rentioTheme.colors.info}>
          <FullScreenLoadingState
            title={getLocalizedText(
              `payment_detail.${paymentMethod}.done.title`,
            )}
            info={getLocalizedText(`payment_detail.${paymentMethod}.done.info`)}
            primaryActionTitle={getLocalizedText("system.confirm")}
            onPressPrimaryAction={() => {
              navigate("/");
            }}
            extraActionTitle={getLocalizedText(
              "payment_detail.bank_wire.done.cta.back",
            )}
            onPressExtraAction={handlePressDoneBack}
          />
        </Page>
      );
    }

    return (
      <Page>
        <InfoHeader
          title={infoUtils.getTitle({
            postfix: "bank_wire",
            paymentRequest,
            property,
          })}
          body={infoUtils.getBody({
            postfix: "bank_wire",
            paymentRequest,
            property,
          })}
          buttonTitle={getLocalizedText(
            "payment.online.bank_wire.cta.info.title",
          )}
          onPressButton={handlePressMoreInfo}
        />
        <S.InnerWrap>
          {paymentMethod === EPaymentMethod.BankWire &&
            renderBankWire(paymentRequest)}

          {paymentMethod === EPaymentMethod.Ibanisation &&
            renderIbanisation(paymentRequest)}

          <Spacer weight={ESpacerWeight.W24} />

          <S.ButtonPrimaryWrap>
            <ButtonPrimary
              title={getLocalizedText("payment_detail.cta.bank_wire_done")}
              onPress={handlePressPaid}
              buttonSize={EButtonSize.Full}
            />
          </S.ButtonPrimaryWrap>
          {paymentRequest.allowedPaymentMethods.length > 1 && (
            <S.ButtonBasicWrap>
              <ButtonBasic
                title={getLocalizedText(
                  "payment_detail.cta.select_other_method",
                )}
                onPress={handlePressOtherMethod}
              />
            </S.ButtonBasicWrap>
          )}
        </S.InnerWrap>
      </Page>
    );
  };

  const renderPaymentRequest = (paymentRequest: IPaymentRequest) => {
    return (
      <PropertyFetchListItem
        shouldRefetch={false}
        propertyId={paymentRequest.requestPropertyId}
        renderAlways={(property?: IProperty) =>
          renderPaymentRequestAndProperty(paymentRequest, property)
        }
      />
    );
  };

  const renderBankWire = (paymentRequest: IPaymentRequest) => {
    if (
      !paymentRequest.allowedPaymentMethods.includes(EPaymentMethod.BankWire)
    ) {
      return null;
    }

    if (!bankWirePayment) {
      return null;
    }

    const {
      bankName,
      bankAddress,
      bankAccount,
      bankBic,
      reference,
      qrContent,
    } = bankWirePayment.metaData;
    const provider = isBanqupPayment
      ? ERentDepositProvider.Banqup
      : ERentDepositProvider.MangoPay;

    return (
      <>
        <S.Title>
          {getLocalizedText("payment.online.bank_wire.info.title")}
        </S.Title>

        <S.InfoBox>
          <S.InfoWrap>
            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.recipient",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                {bankName}
                <S.InfoBoxValueSubtitle>
                  {getLocalizedText(
                    "payment.online.bank_wire.info.recipient.extra",
                    { provider: provider.toLowerCase() },
                  )}
                </S.InfoBoxValueSubtitle>
              </S.InfoBoxValue>
            </S.InfoBoxRow>

            {bankAddress && (
              <S.InfoBoxRow>
                <S.InfoBoxKey>
                  {getLocalizedText(
                    "payment.online.bank_wire.info.recipient_address",
                  ).toUpperCase()}
                </S.InfoBoxKey>
                <S.InfoBoxValue>{bankAddress}</S.InfoBoxValue>
              </S.InfoBoxRow>
            )}

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.bank_account",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>{formatIban(bankAccount)}</S.InfoBoxValue>
            </S.InfoBoxRow>

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.bank_bic",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>{bankBic}</S.InfoBoxValue>
            </S.InfoBoxRow>

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.amount",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                {formatCurrency(paymentRequest.amount)}
              </S.InfoBoxValue>
            </S.InfoBoxRow>

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.reference",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                <S.InfoBoxValueBorder>
                  <S.InfoBoxValueMono>{reference}</S.InfoBoxValueMono>
                </S.InfoBoxValueBorder>
              </S.InfoBoxValue>
            </S.InfoBoxRow>
          </S.InfoWrap>

          {qrContent && (
            <S.QrWrap>
              <QRCode
                value={qrContent}
                size={160}
                bgColor={rentioTheme.colors.global4}
              />
              <S.QrInfoText>
                {getLocalizedText("payment.online.bank_wire.qr_info")}
              </S.QrInfoText>
              <S.QrInfoLogos src={assets.ICO_QR_PROVIDERS} />
            </S.QrWrap>
          )}
        </S.InfoBox>

        <S.BottomInfo
          dangerouslySetInnerHTML={{
            __html: getLocalizedText("payment.online.bank_wire.info.extra"),
          }}
        />
      </>
    );
  };

  const renderIbanisation = (paymentRequest: IPaymentRequest) => {
    if (
      !paymentRequest.allowedPaymentMethods.includes(EPaymentMethod.Ibanisation)
    ) {
      return null;
    }

    if (!ibanisationBankAccount) {
      return null;
    }

    const provider = isBanqupPayment
      ? ERentDepositProvider.Banqup
      : ERentDepositProvider.MangoPay;

    return (
      <>
        <S.Title>
          {getLocalizedText("payment.online.bank_wire.info.title")}
        </S.Title>

        <S.Subtitle>
          {getLocalizedText(
            `payment.online.bank_wire.info.subtitle.ibanisation.${
              paymentRequest.repetitionType === EPaymentRepetitionType.Once
                ? "once"
                : "recurring"
            }`,
          )}
        </S.Subtitle>

        <S.InfoBox>
          <S.InfoWrap>
            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.recipient",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                {/* TODO: Hardcoded mangopay for Ibanisation? */}
                {ibanisationBankAccount.bank ||
                  getLocalizedText(
                    "payment.online.bank_wire.info.mangopay.bank_name",
                  )}
                <S.InfoBoxValueSubtitle>
                  {getLocalizedText(
                    "payment.online.bank_wire.info.recipient.extra",
                    { provider: provider.toLowerCase() },
                  )}
                </S.InfoBoxValueSubtitle>
              </S.InfoBoxValue>
            </S.InfoBoxRow>

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.recipient_address",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                {getLocalizedText(
                  "payment.online.bank_wire.info.mangopay.bank_address",
                )}
              </S.InfoBoxValue>
            </S.InfoBoxRow>

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.bank_account",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                {formatIban(ibanisationBankAccount.iban)}
              </S.InfoBoxValue>
            </S.InfoBoxRow>

            {ibanisationBankAccount.mangoWalletIbanBic && (
              <S.InfoBoxRow>
                <S.InfoBoxKey>
                  {getLocalizedText(
                    "payment.online.bank_wire.info.bank_bic",
                  ).toUpperCase()}
                </S.InfoBoxKey>
                <S.InfoBoxValue>
                  {ibanisationBankAccount.mangoWalletIbanBic}
                </S.InfoBoxValue>
              </S.InfoBoxRow>
            )}

            <S.InfoBoxRow>
              <S.InfoBoxKey>
                {getLocalizedText(
                  "payment.online.bank_wire.info.amount",
                ).toUpperCase()}
              </S.InfoBoxKey>
              <S.InfoBoxValue>
                {formatCurrency(paymentRequest.amount)}
              </S.InfoBoxValue>
            </S.InfoBoxRow>

            {paymentRequest.ibanisationReference && (
              <S.InfoBoxRow>
                <S.InfoBoxKey>
                  {getLocalizedText(
                    "payment.online.bank_wire.info.reference",
                  ).toUpperCase()}
                </S.InfoBoxKey>
                <S.InfoBoxValue>
                  <S.InfoBoxValueBorder>
                    <S.InfoBoxValueMono>
                      {paymentRequest.ibanisationReference}
                    </S.InfoBoxValueMono>
                  </S.InfoBoxValueBorder>
                </S.InfoBoxValue>
              </S.InfoBoxRow>
            )}
          </S.InfoWrap>
        </S.InfoBox>

        <S.BottomInfo
          dangerouslySetInnerHTML={{
            __html: getLocalizedText("payment.online.bank_wire.info.extra"),
          }}
        />
      </>
    );
  };

  const renderLoading = () => (
    <Page withScroll={false}>
      <FullScreenActivityIndicator />
    </Page>
  );

  const renderError = (retry: () => void, error?: Error) => (
    <Page withScroll={false}>
      <FullScreenErrorState
        showIcon={false}
        error={error}
        actionTitle={getLocalizedText("try.again")}
        onPressAction={retry}
      />
    </Page>
  );

  if (!paymentRequestId) {
    return null;
  }

  return (
    <>
      <PaymentRequestRedirect id={paymentRequestId} />
      <PaymentRequestFetch
        id={paymentRequestId}
        renderPaymentRequest={renderPaymentRequest}
        renderLoading={renderLoading}
        renderError={renderError}
        extraData={{
          bankWirePayment,
          fetchBankWireError,
          isFetchingBankWire,
          didCompleteBankWire,
        }}
        paymentRequestV2Enabled={true}
      />
    </>
  );
};

export const BankWireOverview = withAuthenticationCheck(
  withKycRequired(BankWireOverviewBase),
);
